import React, { useEffect, useState } from 'react';
import { MenuIcon, XIcon } from 'lucide-react';
export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      // Detect active section
      const sections = ['home', 'about', 'advocates', 'practice-areas', 'why-choose-us', 'clients', 'contact'];
      const scrollPosition = window.scrollY + 100;
      for (const section of sections) {
        const element = document.getElementById(section === 'home' ? 'hero' : section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId === 'home' ? 'hero' : sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };
  const navLinks = [{
    id: 'home',
    label: 'Home'
  }, {
    id: 'about',
    label: 'About'
  }, {
    id: 'advocates',
    label: 'Our Team'
  }, {
    id: 'practice-areas',
    label: 'Practice Areas'
  }, {
    id: 'why-choose-us',
    label: 'Why Us'
  }, {
    id: 'contact',
    label: 'Contact'
  }];
  return <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-cream/95 backdrop-blur-md shadow-[0_4px_20px_rgba(0,0,0,0.08)] border-b border-[#D4AF37]/10' : 'bg-transparent'}`} data-id="element-164">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-165">
          <div className="flex items-center justify-between h-20" data-id="element-166">
            {/* Logo */}
            <button onClick={() => scrollToSection('home')} className="flex items-center space-x-3 group" data-id="element-167">
              <img src="https://cdn.magicpatterns.com/uploads/gRNdUYNsrwYKSGkPVbsmda/Logo_wanja_advocates.jpg" alt="Wanja Konji & Co. Advocates" className="h-12 object-contain mix-blend-multiply transition-transform duration-300 group-hover:scale-105" data-id="element-168" />
            </button>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1" data-id="element-169">
              {navLinks.map(link => <button key={link.id} onClick={() => scrollToSection(link.id)} className={`relative px-5 py-2 text-sm font-medium tracking-wider uppercase transition-colors duration-300 group ${activeSection === link.id ? 'text-navy' : isScrolled ? 'text-gray-600 hover:text-navy' : 'text-gray-700 hover:text-navy'}`} data-id="element-170">
                  {link.label}
                  <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-[#D4AF37] transition-all duration-300 ${activeSection === link.id ? 'w-8' : 'w-0 group-hover:w-8'}`} data-id="element-171" />
                </button>)}

              <a href="#contact" className="ml-6 px-6 py-2.5 text-xs font-bold tracking-[0.2em] uppercase text-white bg-navy border border-navy hover:bg-transparent hover:text-navy transition-all duration-300 shadow-sm hover:shadow-md" data-id="element-172">
                Get in Touch
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2 text-navy hover:text-[#D4AF37] transition-colors" aria-label="Toggle menu" data-id="element-173">
              {isMobileMenuOpen ? <XIcon className="h-6 w-6" strokeWidth={1.5} data-id="element-174" /> : <MenuIcon className="h-6 w-6" strokeWidth={1.5} data-id="element-175" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`fixed inset-0 bg-navy/95 backdrop-blur-lg z-40 lg:hidden transition-opacity duration-500 ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} onClick={() => setIsMobileMenuOpen(false)} data-id="element-176">
        <div className={`absolute top-20 right-0 bottom-0 w-full max-w-sm bg-cream border-l border-[#D4AF37]/20 shadow-2xl transition-transform duration-500 ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`} onClick={e => e.stopPropagation()} data-id="element-177">
          <div className="flex flex-col h-full p-8" data-id="element-178">
            {/* Mobile Navigation Links */}
            <nav className="flex-1 space-y-2" data-id="element-179">
              {navLinks.map((link, index) => <button key={link.id} onClick={() => scrollToSection(link.id)} className={`w-full text-left px-6 py-4 font-playfair text-2xl transition-all duration-300 border-l-2 ${activeSection === link.id ? 'text-navy border-[#D4AF37] bg-[#D4AF37]/5' : 'text-gray-600 border-transparent hover:border-[#D4AF37]/30 hover:text-navy hover:bg-[#D4AF37]/5'}`} style={{
              transitionDelay: isMobileMenuOpen ? `${index * 50}ms` : '0ms'
            }} data-id="element-180">
                  {link.label}
                </button>)}
            </nav>

            {/* Mobile CTA */}
            <a href="#contact" onClick={() => setIsMobileMenuOpen(false)} className="w-full px-8 py-4 text-center text-sm font-bold tracking-[0.2em] uppercase text-white bg-navy hover:bg-[#D4AF37] transition-colors duration-300 shadow-lg" data-id="element-181">
              Get in Touch
            </a>

            {/* Mobile Footer Info */}
            <div className="mt-8 pt-8 border-t border-gray-200" data-id="element-182">
              <p className="text-sm text-gray-500 text-center font-light" data-id="element-183">
                Applewood Adams, 4th Floor
                <br data-id="element-184" />
                Ngong Road, Nairobi
              </p>
            </div>
          </div>
        </div>
      </div>
    </>;
}